<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetProductGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const productTypes = ref(null);
  const productGroups = ref(null);
  const taxCodes = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_3573211234170528_multiple = computed(() => false);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    productTypes: productTypes.value,
    productGroups: productGroups.value,
    taxCodes: taxCodes.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_3573211234170528_multiple: b_3573211234170528_multiple.value,
  }));

  const $$load_productTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('ProductTypes');
  };
  const $$load_productGroups = async () => {
    return $fn.value
      .fetch('list_group')
      .then((data) => data.data.map((p) => p.name));
  };
  const $$load_taxCodes = async () => {
    return $fn.value
      .fetch('list_tax_code')
      .then((data) => data.map((p) => p.id));
  };
  const $$load_invoiceGroups = async () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'productTypes' },
      { name: 'productGroups' },
      { name: 'taxCodes' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_3573211234170528_multiple', nodeId: '3573211234170528' },
    ],
    loaders: {
      productTypes: $$load_productTypes,
      productGroups: $$load_productGroups,
      taxCodes: $$load_taxCodes,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      labelAlign,
      productTypes,
      productGroups,
      taxCodes,
      invoiceGroups,
      ux,
      b_3573211234170528_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );

  const act_7315092382398562_reloadComponentData_sjx0t5krh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_wrt7cz4l4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_sjx0t5krh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_wrt7cz4l4],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_wrt7cz4l4,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_wrt7cz4l4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_wrt7cz4l4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="name"
      :label="pgettext('Product', 'Type')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="product_type"
        v-model="product.product_type"
        :value="product.product_type"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.product_type =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        :options="productTypes"
        autofocus
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="number"
      :label="pgettext('Product', 'Item number')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="number"
        v-model="product.number"
        :value="product.number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="name"
      :label="pgettext('Product', 'Name')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="name"
        v-model="product.name"
        :value="product.name"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.name =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Product', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="description"
        v-model="product.description"
        :value="product.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="unit"
      :label="pgettext('Product', 'Unit')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="unit"
        v-model="product.unit"
        :value="product.unit"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.unit =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      :label-cols-md="4"
      name="sales_vat_code"
      :label="pgettext('Product', 'Sales tax code')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="sales_vat_code"
        v-model="product.sales_vat_code"
        :value="product.sales_vat_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.sales_vat_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="taxCodes"
        required
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="sales_vat_code"
      :label="pgettext('Product', 'Purchase tax code')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="purchase_vat_code"
        v-model="product.purchase_vat_code"
        :value="product.purchase_vat_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.purchase_vat_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="taxCodes"
        required
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="intermediated_service"
      :label="pgettext('Product', 'Intermediated service')"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox
        name="intermediated_service"
        v-model="product.intermediated_service"
        :value="product.intermediated_service"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.intermediated_service =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormCheckbox>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="invoice_group_id"
      :label="pgettext('Product', 'Invoice Group')"
      :labelAlign="labelAlign"
    >
      <ItpFormSelect2
        name="invoice_group_id"
        v-model="product.invoice_group_id"
        :value="product.invoice_group_id"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.invoice_group_id =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :multiple="b_3573211234170528_multiple"
        :options="invoiceGroups"
        valueField="id"
        textField="name"
        clearable
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="groups"
      :label="pgettext('Product', 'Groups')"
      :labelAlign="labelAlign"
    >
      <ItpSelect2
        name="groups"
        v-model="product.groups"
        :value="product.groups"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.groups =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        multiple
        :options="productGroups"
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="customs_tariff_no"
      :label="pgettext('Product', 'Customs tariff no.')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="customs_tariff_no"
        v-model="product.customs_tariff_no"
        :value="product.customs_tariff_no"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.customs_tariff_no =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="hu_teszor_code"
      :label="pgettext('Product', 'TESZOR code (HU)')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="hu_teszor_code"
        v-model="product.hu_teszor_code"
        :value="product.hu_teszor_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.hu_teszor_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="eu_ewc_code"
      :label="pgettext('Product', 'EWC code (EU)')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="eu_ewc_code"
        v-model="product.eu_ewc_code"
        :value="product.eu_ewc_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.eu_ewc_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="external_key"
      :label="pgettext('Product', 'External key')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="external_key"
        v-model="product.external_key"
        :value="product.external_key"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (product.external_key =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
